import { HttpContext } from '@angular/common/http';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';

import { API_ROUTES } from '../consts/api-routes.const';
import { isServerError } from '../interceptors/api-error.interceptor';
import { SKIP_AUTH_REDIRECT } from '../interceptors/auth-redirect.const';
import { HttpService } from './http.service';
import {
  DEFAULT_LANGUAGE_CID,
  setTranslations,
  toDictionary,
  TRANSLATION_SCOPE,
  TranslationDictionary,
} from './translate';

const LANGUAGE_PARAM = 'lang';
const LANGUAGE_CID_PATTERN = /^[a-z]{2}(-[A-Z]{2})?$/;

/** Language for this page load: `?lang=` when it is a well-formed cid, Dutch otherwise. */
export function resolveLanguageCid(search: string): string {
  const requested: string | null = new URLSearchParams(search).get(LANGUAGE_PARAM);

  return requested !== null && LANGUAGE_CID_PATTERN.test(requested)
    ? requested
    : DEFAULT_LANGUAGE_CID;
}

@Injectable({ providedIn: 'root' })
export class TranslationLoaderService {
  private readonly httpService: HttpService = inject(HttpService);
  private readonly _ready = signal<boolean>(false);
  /** True once load() has settled (copy or empty overlay). App holds its shell loader until then. */
  public readonly ready: Signal<boolean> = this._ready.asReadonly();

  /**
   * Fetches the translation overlay — the app's only source of copy. Fired non-blocking at
   * startup, in parallel with the dealer-config gate; App keeps its loader up until `ready`, so
   * no screen ever paints raw cids. A 5xx runs the shared retry modal like any other call; when
   * that cycle fails too, `ready` stays false so the shell loader stays behind the parked modal
   * instead of a cid screen. Any other failure resolves to an empty overlay and flips `ready`, so
   * the shell never hangs without a modal. Never rejects. Opts out of the 401 redirect because
   * it runs before anything can render. No client timeout on purpose — it would trip on a test's
   * fake clock, and a hung request is the same failure mode as a hung dealer-config call.
   */
  public load(languageCid: string): Observable<TranslationDictionary> {
    const context: HttpContext = new HttpContext().set(SKIP_AUTH_REDIRECT, true);

    return this.httpService
      .get<unknown>(API_ROUTES.CORE.translation(languageCid, TRANSLATION_SCOPE), context)
      .pipe(
        map((payload: unknown): TranslationDictionary => toDictionary(payload)),
        tap((dictionary: TranslationDictionary): void => {
          setTranslations(dictionary, languageCid);
          this._ready.set(true);
        }),
        catchError((error: unknown): Observable<TranslationDictionary> => {
          if (!isServerError(error)) {
            this._ready.set(true);
          }
          return of({});
        }),
      );
  }
}
